import { makeStyles, mergeClasses, ToggleButton, Tooltip, tokens } from '@fluentui/react-components'
import { GridRegular, TableRegular } from '@fluentui/react-icons'
import { useEffect, useState, type ReactNode } from 'react'

export type WorkspaceItemsDisplayMode = 'table' | 'grid'

const storageKey = 'workspace-items-display-mode'
const changeEvent = 'workspace-items-display-mode-change'

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    minWidth: 0,
    flexGrow: 1,
  },
  scrollableRoot: {
    minHeight: 0,
  },
  controls: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: tokens.spacingHorizontalXS,
    flexShrink: 0,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    padding: tokens.spacingHorizontalXXS,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalM,
  },
  tools: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalS,
    marginInlineStart: 'auto',
    flex: '1 1 660px',
    justifyContent: 'flex-end',
    minWidth: 0,
    '@media (max-width: 720px)': {
      width: '100%',
      marginInlineStart: 0,
      justifyContent: 'flex-start',
      flexBasis: '100%',
    },
  },
  content: {
    overflowX: 'auto',
    minWidth: 0,
    flexGrow: 1,
  },
  scrollableContent: {
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    minHeight: 0,
  },
  tableContent: {
    overflowY: 'hidden',
  },
})

function readDisplayMode(): WorkspaceItemsDisplayMode {
  try {
    const savedMode = sessionStorage.getItem(storageKey)
    if (savedMode === 'table' || savedMode === 'grid') {
      return savedMode
    }
    if (savedMode !== null) {
      console.warn('Invalid workspace items display mode in session storage', savedMode)
    }
  } catch (error) {
    console.error('Failed to read workspace items display mode from session storage', error)
  }
  return 'table'
}

export default function WorkspaceItemsView({
  children,
  scrollWithinItems = false,
  leadingActions,
  tools,
  trailingActions,
}: {
  children: (displayMode: WorkspaceItemsDisplayMode) => ReactNode
  scrollWithinItems?: boolean
  leadingActions?: ReactNode
  tools?: ReactNode
  trailingActions?: ReactNode
}) {
  const styles = useStyles()
  const [displayMode, setDisplayMode] = useState<WorkspaceItemsDisplayMode>(readDisplayMode)

  useEffect(() => {
    const onModeChange = (event: Event) => {
      if (event instanceof CustomEvent) {
        const mode: unknown = event.detail
        if (mode === 'table' || mode === 'grid') {
          setDisplayMode(mode)
        }
      }
    }
    window.addEventListener(changeEvent, onModeChange)
    return () => {
      window.removeEventListener(changeEvent, onModeChange)
    }
  }, [])

  const changeDisplayMode = (mode: WorkspaceItemsDisplayMode) => {
    setDisplayMode(mode)
    try {
      sessionStorage.setItem(storageKey, mode)
    } catch (error) {
      console.error('Failed to save workspace items display mode in session storage', error)
    }
    window.dispatchEvent(new CustomEvent(changeEvent, { detail: mode }))
  }

  return (
    <div className={mergeClasses(styles.root, scrollWithinItems && styles.scrollableRoot)}>
      <div className={styles.toolbar}>
        {leadingActions}
        <div className={styles.tools}>
          {tools}
          <div className={styles.controls} role="group" aria-label="Display mode">
            <Tooltip content="Table view" relationship="label">
              <ToggleButton
                size="small"
                appearance="subtle"
                aria-label="Table view"
                checked={displayMode === 'table'}
                icon={<TableRegular />}
                onClick={() => changeDisplayMode('table')}
              />
            </Tooltip>
            <Tooltip content="Grid view" relationship="label">
              <ToggleButton
                size="small"
                appearance="subtle"
                aria-label="Grid view"
                checked={displayMode === 'grid'}
                icon={<GridRegular />}
                onClick={() => changeDisplayMode('grid')}
              />
            </Tooltip>
          </div>
          {trailingActions}
        </div>
      </div>
      <div
        className={mergeClasses(
          styles.content,
          scrollWithinItems && styles.scrollableContent,
          scrollWithinItems && displayMode === 'table' && styles.tableContent,
        )}
      >
        {children(displayMode)}
      </div>
    </div>
  )
}
